import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useTopBarStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[6],
    paddingRight: designTokens.spacing[6],
    backgroundColor: designTokens.neutral.white,
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    flexShrink: 0,
    "@media (max-width: 768px)": {
      paddingTop: designTokens.spacing[3],
      paddingBottom: designTokens.spacing[3],
      paddingLeft: "90px",
      paddingRight: designTokens.spacing[3],
    },
  },
  leftSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
  },
  dropdownTrigger: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    cursor: "pointer",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    transition: `all ${designTokens.transition.fast}`,
    minWidth: 0,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
    "@media (max-width: 900px)": {
      padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
      gap: designTokens.spacing[1],
    },
  },
  dropdownTriggerDisabled: {
    cursor: "not-allowed",
    opacity: 0.5,
    pointerEvents: "none",
    ":hover": {
      backgroundColor: "transparent",
    },
  },
  truncatedTextWrapper: {
    display: "flex",
    alignItems: "center",
    maxWidth: "280px",
    overflow: "hidden",
    minWidth: 0,
    "@media (max-width: 900px)": {
      maxWidth: "none",
    },
  },
  dropdownLabel: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral[700],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chevronIcon: {
    width: "16px",
    height: "16px",
    color: designTokens.neutral[400],
  },
  separator: {
    color: designTokens.neutral[400],
    fontSize: designTokens.fontSize.sm,
  },
  popoverContent: {
    padding: "0",
    maxWidth: "300px",
    minWidth: "200px",
    borderRadius: designTokens.borderRadius.lg,
    overflow: "hidden",
  },
  popoverList: {
    maxHeight: "180px",
    overflowY: "auto",
  },
  popoverHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
  },
  popoverHeaderTitle: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    letterSpacing: "0.5px",
    textAlign: "center",
  },
  popoverFooter: {
    padding: designTokens.spacing[2],
    borderTop: `1px solid ${designTokens.neutral[200]}`,
  },
  popoverItem: {
    height: designTokens.controlHeight.md,
    paddingLeft: designTokens.spacing[4],
    paddingRight: designTokens.spacing[4],
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
  },
  popoverItemCenter: {
    height: designTokens.controlHeight.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  popoverItemSelected: {
    backgroundColor: designTokens.neutral[50],
  },
  popoverItemIcon: {
    color: designTokens.neutral[400],
  },
  popoverItemTextWrapper: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  popoverItemText: {
    display: "block",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  checkIcon: {
    color: designTokens.accent[500],
  },
  rightSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[3],
    flexShrink: 0,
  },
  manageContentButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "34px",
    gap: designTokens.spacing[2],
    "& > .fui-Button__icon": {
      marginRight: "2px",
    },
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[4],
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    backgroundColor: designTokens.primary[900],
    color: designTokens.neutral.white,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    borderRadius: designTokens.borderRadius.md,
    ":hover": {
      backgroundColor: designTokens.primary[700],
      color: designTokens.neutral.white,
    },
    ":active": {
      backgroundColor: designTokens.primary[900],
      color: designTokens.neutral.white,
    },
    ":hover:active": {
      backgroundColor: designTokens.primary[900],
      color: designTokens.neutral.white,
    },
    "@media (max-width: 900px)": {
      paddingLeft: designTokens.spacing[2],
      paddingRight: designTokens.spacing[2],
      minWidth: "34px",
      justifyContent: "center",
      "& > .fui-Button__icon": {
        marginRight: "0",
      },
    },
  },
  manageContentButtonText: {
    "@media (max-width: 900px)": {
      display: "none",
    },
  },
  tooltipContent: {
    maxWidth: "280px",
    display: "block",
    wordBreak: "break-word",
  },
  newChatButtonSize: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: "0",
    ":disabled": {
      backgroundColor: designTokens.neutral[300],
      color: designTokens.neutral.white,
      cursor: "not-allowed",
      opacity: 0.6,
    },
    ":disabled:hover": {
      backgroundColor: designTokens.neutral[300],
      color: designTokens.neutral.white,
    },
  },
  shareButtonSize: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: "0",
    boxSizing: "border-box",
    ":disabled": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[400],
      borderTopColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: "transparent",
      borderLeftColor: "transparent",
      cursor: "not-allowed",
      opacity: 0.6,
    },
    ":disabled:hover": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[400],
      borderTopColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: "transparent",
      borderLeftColor: "transparent",
    },
  },
  popoverActionButton: {
    paddingRight: "0",
  },
  badge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
  },
});
